/**
 * AI operations panel for the Overview tab.
 *
 * Shows month-to-date usage of inference + training operations.
 * Unlike the data meters above, AI ops have NO included monthly
 * quota — every call costs credits. This panel exists so users
 * can see their consumption pattern at a glance without digging
 * through the Activity tab.
 *
 * Data source: ``snapshot.ai_usage`` (computed from credit_transactions
 * by the backend in app/billing/credits.charge audit rows).
 */

import React from 'react';
import { Box, Typography, Stack, Divider, Chip, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import { TrainingsIcon } from '../global/CustomIcons';

import { describeActivity, formatCredits, CATEGORY_COLORS } from './billingHelpers';

const Tile = ({ icon, label, count, credits, breakdown }) => {
  const color = label === 'Inferences' ? CATEGORY_COLORS.Inference : CATEGORY_COLORS.Training;
  const avg = count > 0 ? credits / count : 0;
  return (
    <Box sx={{
      flex: 1,
      p: 2.5,
      borderRadius: 2,
      bgcolor: (t) => alpha(color, 0.05),
      border: (t) => `1px solid ${alpha(color, 0.18)}`,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1,
          bgcolor: alpha(color, 0.15), color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ color }}>
          {label}
        </Typography>
        <Box flex={1} />
        <Tooltip title="Pay-as-you-go: no monthly included quota. Every call charges credits.">
          <Chip size="small" label="usage" sx={{
            height: 20, fontSize: 10,
            bgcolor: alpha(color, 0.12), color, fontWeight: 600,
          }} />
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>
            {count.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label === 'Inferences' ? 'calls this period' : 'jobs this period'}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto !important', textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color }}>
            {formatCredits(credits)}
          </Typography>
          <Typography variant="caption" color="text.secondary">credits spent</Typography>
        </Box>
      </Stack>

      {count > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Avg {avg.toFixed(2)} credits / {label === 'Inferences' ? 'call' : 'job'}
        </Typography>
      )}

      {Object.keys(breakdown || {}).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={0.5}>
            {Object.entries(breakdown).slice(0, 4).map(([kind, b]) => {
              const friendly = describeActivity(
                label === 'Inferences' ? `inference.${kind}` : `adapter_training.${kind}`,
              ).label;
              return (
                <Stack key={kind} direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {friendly}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {b.count.toLocaleString()} ·{' '}
                  </Typography>
                  <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
                    {formatCredits(b.credits)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
};

const AIOpsPanel = ({ aiUsage }) => {
  const inference = aiUsage?.inference || { count: 0, credits_spent: 0, by_kind: {} };
  const training  = aiUsage?.training  || { count: 0, credits_spent: 0, by_kind: {} };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Inference and training are pay-as-you-go. Each call charges credits
        at the rate set by your plan (open Pricing for per-unit prices).
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Tile
          icon={<BoltIcon fontSize="small" />}
          label="Inferences"
          count={inference.count}
          credits={inference.credits_spent}
          breakdown={inference.by_kind}
        />
        <Tile
          icon={<TrainingsIcon fontSize="small" />}
          label="Trainings"
          count={training.count}
          credits={training.credits_spent}
          breakdown={training.by_kind}
        />
      </Stack>
    </Box>
  );
};

export default AIOpsPanel;
